// Data-laag van het Dagstart-scherm: ophalen, bufferen, en optimistisch bijwerken.
//
// Drie lagen boven elkaar, bewust gescheiden:
//   serverTaken  wat /Api/dagstart teruggaf
//   overlays     velden die wij zojuist hebben gewijzigd en die de server nog niet kent
//   → taken      wat het scherm rendert
//
// De overlay bestaat omdat een dagstart hardop gaat: je zegt "die schuift naar vandaag",
// klikt, en het blok moet meteen verspringen. Wachten op Airtable + een volledige refetch
// is anderhalve seconde stilte voor een meekijkende zaal.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { BACKEND_URL } from '../../utils/apiConfig';
import { formatDateISO, addDays } from '../../utils/weekUtils';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { createWeekCache } from '../../utils/weekCache';
import { beoordeelOverlays, conflictMelding } from './conflicten';
import { metOutlookAfspraken } from './outlookAfspraken';
import { toonOngedaanToast } from './undoToast';

const PERSONEN_URL = `${BACKEND_URL}/Api/planner-todo/persons`;

/** Velden die we optimistisch kunnen spiegelen: Airtable-veld → veld in onze taak. */
const OVERLAY_MAPPING = {
  StartDateUitvoerder: 'startDate',
  EndDateUitvoerder: 'endDate',
  PrioNr: 'prio',
  Afgerond: 'isComplete',
  UrenWerk: 'hours',
  Deadline: 'deadline',
  TypeDeadline: 'deadlineType',
};

const naarOverlay = (velden) => {
  const uit = {};
  Object.entries(velden || {}).forEach(([airtableVeld, waarde]) => {
    const eigen = OVERLAY_MAPPING[airtableVeld];
    if (eigen) uit[eigen] = waarde;
  });
  return uit;
};

/** Hoe vaak we op de achtergrond kijken of een collega iets heeft verzet. */
const ACHTERGROND_MS = 60 * 1000;

/**
 * Wachttijd voordat we de buurvensters ophalen. Het zichtbare venster moet eerst binnen
 * zijn: drie gelijktijdige Graph-sweeps maken de klik waar iemand op wacht juist trager,
 * en Graph throttelt op agenda-uren.
 */
const VOORUITLADEN_MS = 800;

/**
 * Rust na je laatste wijziging voordat we het zichtbare venster stil bijwerken.
 *
 * Lang genoeg dat een reeks verplaatsingen (of drie klikken in het detailpaneel) tot één
 * ronde samenvalt, kort genoeg dat je binnen een tel de echte serverwaarde ziet in plaats
 * van je eigen overlay.
 */
const NA_SCHRIJVEN_MS = 900;

/**
 * @param buren          [{start, eind}] — vensters die waarschijnlijk als volgende in
 *                       beeld komen (een werkdag terug en vooruit). Die halen we stil op
 *                       de achtergrond op, zodat ‹ en › uit de buffer komen.
 * @param magVernieuwen  () => boolean — het scherm zegt of een achtergrondronde nú mag.
 *                       Midden in een sleepgebaar of met een openstaand detailpaneel is
 *                       het antwoord nee: data die onder je handen verschuift is erger
 *                       dan data die dertig seconden oud is.
 */
/** Hoeveel kalenderdagen ná het venster de capaciteit meerekent (≈ twee maanden). */
const HORIZON_DAGEN = 61;
/** Zo lang mag een opgehaalde horizon hergebruikt worden (buurvensters, 60 s-rondes). */
const HORIZON_TTL_MS = 5 * 60 * 1000;

export function useDagstartData({ start, eind, buren, magVernieuwen }) {
  const [laden, setLaden] = useState(true);
  // Of er ooit een venster binnen is gekomen. Zolang dat niet zo is hoort er een spinner
  // te staan; daarna niet meer — zie de toelichting bij de return.
  const [ooitGeladen, setOoitGeladen] = useState(false);
  const [fout, setFout] = useState(null);
  const [server, setServer] = useState({ assignees: [], tasksByAssignee: {} });
  const [agendaUren, setAgendaUren] = useState({});
  // Taken per persoon in de twee maanden ná het venster — alleen voor de capaciteitsrij (#62).
  const [horizonPerPersoon, setHorizonPerPersoon] = useState({});
  // Niet stil falen: als Outlook niet antwoordt kloppen de percentages niet, en dat
  // hoort het scherm te zeggen in plaats van een te laag getal te tonen.
  const [agendaFout, setAgendaFout] = useState(null);
  const [personen, setPersonen] = useState([]);
  const [overlays, setOverlays] = useState({});

  // Personen zijn periode-onafhankelijk (we hebben ze alleen voor e-mail en functie);
  // één keer ophalen en de belofte hergebruiken, ook door de vooruitgeladen periodes.
  const personenRef = useRef(null);
  const [personenFout, setPersonenFout] = useState(false);
  const laadPersonen = useCallback(() => {
    if (!personenRef.current) {
      personenRef.current = authFetch(PERSONEN_URL)
        .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then((d) => { setPersonenFout(false); return d.persons || []; })
        .catch(() => {
          personenRef.current = null; // volgende poging mag het opnieuw proberen
          setPersonenFout(true); // FE-06: niet stil [] — het scherm meldt het
          return [];
        });
    }
    return personenRef.current;
  }, []);

  /**
   * Agenda per venster, los van de takenbuffer.
   *
   * Een Airtable-taak verzetten verandert niemands Outlook-agenda. Toch haalde elke
   * verversing ná een verplaatsing de hele Graph-sweep over alle mailboxen opnieuw op —
   * de traagste en meest throttelgevoelige call van dit scherm, voor data die
   * gegarandeerd hetzelfde is. Die bewaren we hier zodat een taken-ronde 'm kan overslaan.
   */
  const agendaRef = useRef(new Map());
  const bewaarAgenda = useCallback((sleutel, waarde) => {
    agendaRef.current.set(sleutel, waarde);
    while (agendaRef.current.size > 12) {
      agendaRef.current.delete(agendaRef.current.keys().next().value);
    }
  }, []);

  /**
   * De taken van de twee maanden ná het venster, alleen voor de capaciteitsrij. Een meerdaagse
   * taak die voorbij de laatste zichtbare dag doorloopt kon zijn uren nergens anders kwijt dan
   * op de zichtbare dagen → te hoge (of bij verlof te lage) percentages (tool-feedback #62,
   * 23 sep 2026). Eén ophaling per week, gedeeld door de buurvensters en de 60 s-rondes: op
   * main gaat /Api/dagstart live naar Airtable, en elke stap ‹ › opnieuw twee maanden vragen
   * kost daar rate-limit. Fail-soft: zonder horizon rekent de rij zoals voorheen.
   */
  const horizonCacheRef = useRef(new Map());
  const haalHorizon = useCallback((tot) => {
    const d = new Date(`${tot}T12:00:00`);
    const maandag = addDays(d, -((d.getDay() + 6) % 7));
    const sleutel = formatDateISO(maandag);
    const bewaard = horizonCacheRef.current.get(sleutel);
    if (bewaard && Date.now() - bewaard.tijd < HORIZON_TTL_MS) return bewaard.belofte;
    const belofte = authFetch(`${BACKEND_URL}/Api/dagstart?start=${sleutel}&end=${formatDateISO(addDays(maandag, HORIZON_DAGEN + 7))}&scope=venster`)
      .then((r) => (r.ok ? r.json() : null))
      .then((dd) => (dd && dd.tasksByAssignee) || {})
      .catch(() => ({}));
    horizonCacheRef.current.set(sleutel, { tijd: Date.now(), belofte });
    return belofte;
  }, []);

  /**
   * @param hergebruikAgenda  alleen de taken opnieuw ophalen en de eerder opgehaalde
   *                          agenda ernaast leggen. Voor rondes ná een eigen wijziging.
   */
  const laadPeriode = useCallback(async (vanaf, tot, { hergebruikAgenda = false, onDeel } = {}) => {
    const haalTak = (scope) =>
      authFetch(`${BACKEND_URL}/Api/dagstart?start=${vanaf}&end=${tot}&scope=${scope}`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      });

    // Tweetraps (het scherm laadde "een tikkeltje te lang", 26 aug): eerst
    // alleen het venster — de snelste van de drie Airtable-takken — zodat het
    // grid direct staat; backlog (achterstallig/ongepland) komt er daarna
    // onder geschoven, parallel aan de Graph-agenda.
    const [venster, lijst] = await Promise.all([haalTak('venster'), laadPersonen()]);
    onDeel?.({ taken: venster, personen: lijst, agendaUren: {} });

    const backlogBelofte = haalTak('backlog').catch(() => haalTak('backlog'));
    // Horizon: de taken van de twee maanden ná het venster, alleen voor de capaciteitsrij
    // (tool-feedback #62). Gedeeld per week, zie haalHorizon.
    const horizonBelofte = haalHorizon(tot);

    // Agenda-uren komen uit Outlook via Graph en zijn de traagste én meest fragiele
    // schakel; daarom apart, ná de taken, en fail-soft. Een dagstart zonder
    // meeting-uren is bruikbaar, een dagstart zonder taken niet. De afspraken zelf komen
    // in dezelfde ronde mee: één Graph-sweep voor de uren én de blokken, zodat de
    // capaciteitsrij en de AFSPRAKEN-lane per definitie dezelfde agenda tonen.
    const vensterSleutel = `${vanaf}|${tot}`;
    const bewaard = hergebruikAgenda ? agendaRef.current.get(vensterSleutel) : null;

    const agendaBelofte = (async () => {
      if (bewaard) return bewaard;
      const emails = lijst.map((p) => p.email).filter(Boolean);
      const agenda = emails.length
        ? await authFetch(`${BACKEND_URL}/Api/planner-todo/calendar-hours`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ emails, startDate: vanaf, endDate: tot, includeEvents: true }),
          })
            .then((r) => leesJson(r, 'Agenda-uren laden'))
            // Achtergrondrondes herhalen dit: geen toast per tik, wel zichtbaar in de console.
            .catch((e) => {
              // Fail-soft, maar niet stil: het scherm toont een regel boven de
              // capaciteitsrij. Zonder die melding stond er drie dagen lang een te
              // laag percentage zonder dat iemand het doorhad (5-8 sep).
              console.warn('[dagstart] agenda-uren niet geladen:', e.message);
              return { __fout: e.message || 'onbekende fout' };
            })
        : {};
      const agendaFout = agenda?.__fout || null;
      const uren = agenda?.hours || {};
      const events = agenda?.events || null;

      // Graph antwoordt op e-mailadres, het scherm denkt in voornamen.
      const perNaam = {};
      const eventsPerNaam = events ? {} : null;
      lijst.forEach((p) => {
        const naam = p.firstName || p.name;
        if (!naam) return;
        const sleutel = (p.email || '').toLowerCase();
        perNaam[naam] = uren[sleutel] || {};
        // Alleen overnemen als Graph deze mailbox écht heeft beantwoord; anders blijven
        // de Airtable-afspraken staan (zie metOutlookAfspraken).
        if (eventsPerNaam && Array.isArray(events[sleutel])) eventsPerNaam[naam] = events[sleutel];
      });
      bewaarAgenda(vensterSleutel, { perNaam, eventsPerNaam, agendaFout });
      return { perNaam, eventsPerNaam, agendaFout };
    })();

    const [backlog, { perNaam, eventsPerNaam, agendaFout }, horizon] = await Promise.all([backlogBelofte, agendaBelofte, horizonBelofte]);

    // Venster + backlog samenvoegen: taken ontdubbelen op id, personen samenvoegen.
    const samengevoegd = { ...venster };
    if (backlog) {
      const perId = new Map();
      const namen = new Map((venster.assignees || []).map((a) => [a.naam, a]));
      (backlog.assignees || []).forEach((a) => { if (!namen.has(a.naam)) namen.set(a.naam, a); });
      const perPersoon = {};
      namen.forEach((a, naam) => {
        const alles = [...(venster.tasksByAssignee?.[naam] || []), ...(backlog.tasksByAssignee?.[naam] || [])];
        const uniek = [];
        alles.forEach((t) => { if (!perId.has(`${naam}|${t.id}`)) { perId.set(`${naam}|${t.id}`, true); uniek.push(t); } });
        perPersoon[naam] = uniek;
      });
      samengevoegd.assignees = [...namen.values()]
        .sort((a, b) => (a.isBak === b.isBak ? a.naam.localeCompare(b.naam) : a.isBak ? 1 : -1));
      samengevoegd.instelbaarVerborgen = [...new Set([
        ...(venster.instelbaarVerborgen || []), ...(backlog.instelbaarVerborgen || []),
      ])].sort((a, b) => a.localeCompare(b));
      samengevoegd.tasksByAssignee = perPersoon;
      samengevoegd.totalTasks = Object.values(perPersoon).reduce((s, t) => s + t.length, 0);
    }

    const metAgenda = {
      ...samengevoegd,
      tasksByAssignee: metOutlookAfspraken(samengevoegd?.tasksByAssignee, eventsPerNaam),
    };

    return { taken: metAgenda, personen: lijst, agendaUren: perNaam, agendaFout: agendaFout || null, horizon: horizon || {} };
  }, [laadPersonen, bewaarAgenda, haalHorizon]);

  const bufferRef = useRef(createWeekCache());
  // Stapel met terugzet-acties, nieuwste achteraan. Elke undo-toast houdt zijn eigen
  // item vast, zodat de knop op een oudere melding niet per ongeluk de nieuwste actie
  // terugdraait als je twee dingen snel achter elkaar doet.
  const ongedaanRef = useRef([]);

  // De buurvensters lopen mee met start/eind, maar mogen de ophaal-effect niet opnieuw
  // laten vuren; vandaar een ref en niet de dependency-lijst.
  const burenRef = useRef(buren);
  useEffect(() => { burenRef.current = buren; }, [buren]);
  const vooruitRef = useRef(null);

  /**
   * De vensters van ‹ en › alvast in de buffer zetten. `prefetch` slaat over wat er al
   * ligt of al onderweg is, dus stap je een dag op, dan is er hooguit één nieuw venster
   * bij te halen — geen sneeuwbal.
   */
  const laadBurenVooruit = useCallback(() => {
    clearTimeout(vooruitRef.current);
    vooruitRef.current = setTimeout(() => {
      (burenRef.current || []).forEach((b) => {
        if (!b?.start || !b?.eind) return;
        bufferRef.current.prefetch(`${b.start}|${b.eind}`, () => laadPeriode(b.start, b.eind));
      });
    }, VOORUITLADEN_MS);
  }, [laadPeriode]);

  useEffect(() => () => clearTimeout(vooruitRef.current), []);

  const haalOp = useCallback(async ({ ververs = false } = {}) => {
    if (ververs) {
      bufferRef.current.clear();
      personenRef.current = null;
    }
    const sleutel = `${start}|${eind}`;
    const uitBuffer = bufferRef.current.get(sleutel);
    setFout(null);
    setLaden(!uitBuffer);

    try {
      const res = uitBuffer ?? (await bufferRef.current.load(sleutel, () => laadPeriode(start, eind, {
        // Venster-deel direct tonen zodra het er is — backlog en agenda schuiven
        // vanzelf onder wanneer de volledige ronde klaar is.
        onDeel: (deel) => {
          setServer(deel.taken);
          setPersonen(deel.personen);
          setOoitGeladen(true);
          setLaden(false);
        },
      })));
      setServer(res.taken);
      setPersonen(res.personen);
      setAgendaUren(res.agendaUren);
      setAgendaFout(res.agendaFout || null);
      setHorizonPerPersoon(res.horizon || {});
      setOoitGeladen(true);
      laadBurenVooruit();
      // Na een verse serverronde zijn onze overlays overbodig: wat gelukt is staat er
      // nu in, en wat mislukt is moet níet blijven hangen als schijnwaarheid. De
      // undo-stapel gaat mee weg: na een handmatige verversing weet je niet meer of de
      // opgeslagen "vorige" waarde nog de waarde is waar iemand naar terug wil.
      if (ververs) { setOverlays({}); basisRef.current = {}; ongedaanRef.current = []; }
    } catch (err) {
      setFout(`Kon de dagstart niet laden: ${err.message}`);
    } finally {
      setLaden(false);
    }
  }, [start, eind, laadPeriode, laadBurenVooruit]);

  useEffect(() => { haalOp(); }, [haalOp]);

  /** Server-taken met onze eigen, nog niet bevestigde wijzigingen eroverheen. */
  const takenPerPersoon = useMemo(() => {
    if (!Object.keys(overlays).length) return server.tasksByAssignee || {};
    const uit = {};
    Object.entries(server.tasksByAssignee || {}).forEach(([naam, taken]) => {
      uit[naam] = taken.map((t) => (overlays[t.id] ? { ...t, ...overlays[t.id] } : t));
    });
    return uit;
  }, [server.tasksByAssignee, overlays]);

  /** Alle servertaken plat op id — voor conflictdetectie, die niet per persoon denkt. */
  const takenPerId = useMemo(() => {
    const uit = {};
    Object.values(server.tasksByAssignee || {}).forEach((taken) => {
      (taken || []).forEach((t) => { uit[t.id] = t; });
    });
    return uit;
  }, [server.tasksByAssignee]);

  // Refs naast de state: de achtergrondronde draait in een interval en zou anders met de
  // waarden werken die golden toen het interval werd opgezet.
  const takenPerIdRef = useRef(takenPerId);
  useEffect(() => { takenPerIdRef.current = takenPerId; }, [takenPerId]);
  const overlaysRef = useRef(overlays);
  useEffect(() => { overlaysRef.current = overlays; }, [overlays]);
  // id → `lastModified` zoals de server hem gaf vlak vóór onze eigen wijziging.
  const basisRef = useRef({});

  const zetOverlay = useCallback((id, velden) => {
    if (!(id in basisRef.current)) {
      basisRef.current[id] = takenPerIdRef.current[id]?.lastModified || null;
    }
    setOverlays((prev) => ({ ...prev, [id]: { ...prev[id], ...velden } }));
  }, []);

  const gooiOverlayWeg = useCallback((id) => {
    delete basisRef.current[id];
    setOverlays((prev) => {
      const { [id]: weg, ...rest } = prev;
      return rest;
    });
  }, []);

  // Indirectie omdat werkTaakBij en maakOngedaan elkaar nodig hebben; zonder deze ref
  // verwijzen de twee useCallbacks circulair naar elkaar.
  const herstelRef = useRef(null);

  // Eén wachtrij per taak: twee schrijfacties op hetzelfde blok gaan ná elkaar naar
  // Airtable, nooit tegelijk. Sleep je een taak twee keer binnen een seconde, dan kan de
  // trage eerste PATCH anders ná de tweede landen en staat de taak op de datum die je
  // net verliet. Verschillende taken houden hun eigen rij en blijven dus parallel.
  const rijenRef = useRef(new Map());
  const inDeRij = useCallback((taskId, actie) => {
    const vorige = rijenRef.current.get(taskId) || Promise.resolve();
    const volgende = vorige.then(actie, actie);
    rijenRef.current.set(taskId, volgende);
    // `.catch` en niet `.finally`: die laatste geeft een nieuwe promise terug die bij een
    // afgeketste actie als "unhandled rejection" in de console belandt.
    volgende.then(() => {}, () => {}).then(() => {
      // Alleen opruimen als er niets meer achter ons is aangehaakt.
      if (rijenRef.current.get(taskId) === volgende) rijenRef.current.delete(taskId);
    });
    return volgende;
  }, []);

  /** Wat er met een verse serverronde moet gebeuren — zowel de stille als de 60s-ronde. */
  const verwerkRonde = useCallback((res) => {
    setServer(res.taken);
    setPersonen(res.personen);
    setAgendaUren(res.agendaUren);
    setAgendaFout(res.agendaFout || null);
    setHorizonPerPersoon(res.horizon || {});

    const platteTaken = {};
    Object.values(res.taken?.tasksByAssignee || {}).forEach((taken) => {
      (taken || []).forEach((t) => { platteTaken[t.id] = t; });
    });
    const { weg, conflicten } = beoordeelOverlays({
      takenPerId: platteTaken,
      overlays: overlaysRef.current,
      basis: basisRef.current,
    });
    if (weg.length) {
      setOverlays((prev) => {
        const uit = { ...prev };
        weg.forEach((id) => { delete uit[id]; delete basisRef.current[id]; });
        return uit;
      });
    }
    conflicten.forEach((c) => {
      // De serverwaarde wint, maar nooit stilzwijgend: op een gedeeld scherm is een blok
      // dat zomaar verspringt een minuut zoeken naar wat er nou gebeurde.
      toast(conflictMelding(c), { icon: '↻', duration: 7000 });
      // De opgeslagen "vorige" waarde slaat nergens meer op zodra een ander eroverheen
      // is gegaan — undo zou de wijziging van je collega ongedaan maken.
      ongedaanRef.current = ongedaanRef.current.filter((x) => x.taskId !== c.id);
    });
  }, []);

  // Het venster zoals het nú in beeld staat, voor het stille bijwerken hieronder. Als ref
  // en niet als dependency: anders zou elke stap naar links of rechts de wachtende ronde
  // opnieuw opzetten.
  const vensterRef = useRef({ start, eind });
  useEffect(() => { vensterRef.current = { start, eind }; }, [start, eind]);
  const stilRef = useRef(null);

  /**
   * Na een eigen wijziging het zichtbare venster stil opnieuw ophalen.
   *
   * Hiervoor gooide elke schrijfactie de héle buffer weg en haalde meteen de buurvensters
   * op. Dat was twee keer verkeerd. Traag, want elke verplaatsing trok twee volledige
   * rondes inclusief Graph-sweep op gang terwijl je aan het slepen was. En onbetrouwbaar,
   * want die buurrondes vertrokken vaak vóórdat Airtable onze wijziging teruggaf: de oude
   * datum kwam dan in de buffer en bleef daar twee minuten staan. Bladerde je heen en
   * terug, dan was je verplaatsing "weg" en moest je verversen.
   *
   * Nu: alleen het venster dat je ziet, zonder de agenda opnieuw op te halen, en pas als
   * je even niets meer doet.
   */
  const stilVerversen = useCallback(() => {
    clearTimeout(stilRef.current);
    stilRef.current = setTimeout(async () => {
      // Nog een PATCH onderweg: die wil je eerst laten landen, anders haal je een
      // tussenstand op en knippert het blok terug.
      if (rijenRef.current.size) { stilVerversen(); return; }
      const { start: vanaf, eind: tot } = vensterRef.current;
      let res;
      try {
        res = await laadPeriode(vanaf, tot, { hergebruikAgenda: true });
      } catch {
        return; // Mislukt is geen ramp: de overlay staat er nog en de 60s-ronde komt zo.
      }
      bufferRef.current.set(`${vanaf}|${tot}`, res);
      verwerkRonde(res);
      laadBurenVooruit();
    }, NA_SCHRIJVEN_MS);
  }, [laadPeriode, verwerkRonde, laadBurenVooruit]);

  useEffect(() => () => clearTimeout(stilRef.current), []);

  /**
   * De vensters waar deze taak in kan vallen uit de buffer halen. Gericht, want `clear()`
   * gooit ook de periodes weg waar niets aan veranderde — en die opnieuw ophalen is
   * precies de vertraging die je tijdens een dagstart voelt.
   */
  const vergeetVensters = useCallback(() => {
    const { start: vanaf, eind: tot } = vensterRef.current;
    bufferRef.current.delete(`${vanaf}|${tot}`);
    (burenRef.current || []).forEach((b) => {
      if (b?.start && b?.eind) bufferRef.current.delete(`${b.start}|${b.eind}`);
    });
  }, []);

  /**
   * Eén taak bijwerken via de bestaande PATCH /Api/tasks/:id.
   * Bij een fout gooien we alléén de overlay weg — het hele scherm herladen midden in
   * een dagstart is een veel grovere ingreep dan één blok dat terugspringt.
   *
   * `terug` bevat de velden zoals ze vóór deze wijziging waren. Geef je dat mee, dan
   * wordt de melding een undo-melding en komt de actie op de Cmd+Z-stapel.
   */
  const werkTaakBij = useCallback(async (taskId, velden, { melding, terug } = {}) => {
    // Een afspraak uit Outlook heeft geen Airtable-record; een PATCH hierop is een
    // gegarandeerde 404. De UI biedt het niet aan (afspraken zijn niet sleepbaar en
    // hebben geen vinkje), maar dit is het enige punt waar alle schrijfacties
    // langskomen — dus hier vangen, niet op vier plekken in de UI.
    if (String(taskId).startsWith('outlook:')) {
      toast('Deze afspraak staat in Outlook — daar aanpassen', { icon: '📅' });
      return false;
    }
    const vorige = overlays[taskId];
    // De overlay meteen zetten, buiten de wachtrij om: het blok moet direct verspringen,
    // ook als er nog een PATCH op dezelfde taak onderweg is. Alleen het netwerkdeel
    // wacht op zijn beurt.
    zetOverlay(taskId, naarOverlay(velden));
    return inDeRij(taskId, async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/tasks/${taskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(velden),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        // Het antwoord bevat het record zoals Airtable het nu kent, mét een verse
        // `LastModified`. Dat wordt ons nieuwe ijkpunt: zonder deze regel blijft het
        // ijkpunt de waarde van vóór onze eigen schrijfactie, ziet de eerstvolgende ronde
        // de server als "nieuwer" en meldt hij onze eigen verplaatsing als een wijziging
        // van iemand anders — waarna het blok terugsprong. Precies het "soms zie je je
        // resultaat niet" uit de dagstart.
        const uitslag = await r.json().catch(() => null);
        const nieuwIjkpunt = uitslag?.task?.fields?.LastModified;
        if (nieuwIjkpunt) basisRef.current[taskId] = nieuwIjkpunt;
        // De buffer bevat nu verouderde datums voor de periodes waar deze taak in valt;
        // laat die niet terugkomen bij het volgende bladeren. Daarna één stille ronde over
        // het zichtbare venster — niet meteen twee volledige rondes inclusief Graph-sweep.
        vergeetVensters();
        stilVerversen();
        if (terug) {
          const item = { taskId, velden: terug, melding };
          ongedaanRef.current.push(item);
          toonOngedaanToast({
            melding: melding || 'Bijgewerkt',
            onOngedaan: () => herstelRef.current?.(item),
          });
        } else if (melding) {
          toast.success(melding);
        }
        return true;
      } catch (err) {
        if (vorige) zetOverlay(taskId, vorige); else gooiOverlayWeg(taskId);
        toast.error('Kon de taak niet bijwerken');
        return false;
      }
    });
  }, [overlays, zetOverlay, gooiOverlayWeg, inDeRij, vergeetVensters, stilVerversen]);

  /**
   * Eén actie terugdraaien. Zelf géén `terug` meegeven: een undo hoort niet opnieuw op
   * de stapel te komen, anders zit je met Cmd+Z in een lus tussen twee toestanden.
   */
  const maakOngedaan = useCallback(async (item) => {
    const doel = item || ongedaanRef.current[ongedaanRef.current.length - 1];
    if (!doel) {
      toast('Niets om ongedaan te maken', { icon: 'ℹ️' });
      return false;
    }
    ongedaanRef.current = ongedaanRef.current.filter((x) => x !== doel);
    return werkTaakBij(doel.taskId, doel.velden, { melding: 'Teruggezet' });
  }, [werkTaakBij]);

  useEffect(() => { herstelRef.current = maakOngedaan; }, [maakOngedaan]);

  /**
   * Elke minuut kijken of een collega iets heeft verzet.
   *
   * Vier redenen om over te slaan, want een ronde die data onder iemands handen vervangt
   * is schadelijker dan een scherm dat een minuut oud is: een lopend gebaar of open
   * paneel (dat weet het scherm, via `magVernieuwen`), een tab op de achtergrond, en een
   * PATCH die nog onderweg is — die laatste zou gegarandeerd een schijnconflict opleveren.
   */
  const magRef = useRef(magVernieuwen);
  useEffect(() => { magRef.current = magVernieuwen; }, [magVernieuwen]);

  useEffect(() => {
    const tik = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (rijenRef.current.size) return;
      if (magRef.current && !magRef.current()) return;

      let res;
      try {
        res = await laadPeriode(start, eind);
      } catch {
        return; // Een mislukte achtergrondronde is geen nieuws; de volgende tik komt zo.
      }
      bufferRef.current.set(`${start}|${eind}`, res);
      verwerkRonde(res);
    };

    const id = setInterval(tik, ACHTERGROND_MS);
    return () => clearInterval(id);
  }, [start, eind, laadPeriode, verwerkRonde]);

  /** Meerdere taken tegelijk verzetten (de achterstallig-lane in één klik). */
  const verzetInBulk = useCallback(async (items) => {
    items.forEach((i) => zetOverlay(i.id, {
      startDate: i.StartDateUitvoerder,
      endDate: i.EndDateUitvoerder,
    }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/dagstart/bulk-move`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const uitslag = await r.json();
      // Wat niet gelukt is mag niet als verzet op het scherm blijven staan.
      (uitslag.mislukt || []).forEach((m) => gooiOverlayWeg(m.id));
      // Zelfde ijkpunt-verschuiving als bij de enkelvoudige PATCH: zonder dit meldt de
      // eerstvolgende ronde twintig taken als "aangepast door iemand anders".
      (uitslag.gelukt || []).forEach((g) => {
        if (g?.id && g.lastModified) basisRef.current[g.id] = g.lastModified;
      });
      vergeetVensters();
      stilVerversen();
      return uitslag;
    } catch (err) {
      items.forEach((i) => gooiOverlayWeg(i.id));
      toast.error('Verzetten mislukt');
      return { gelukt: [], mislukt: items.map((i) => ({ id: i.id, naam: i.naam, reden: err.message })) };
    }
  }, [zetOverlay, gooiOverlayWeg, vergeetVensters, stilVerversen]);

  return {
    // Twee soorten "laden", omdat het scherm er verschillend op hoort te reageren. De
    // eerste keer is er niets te tonen: spinner. Daarna hoort de tijdlijn te blijven staan
    // terwijl het nieuwe venster binnenkomt — een klik op ‹ maakte het scherm anders leeg
    // en bouwde alles opnieuw op, wat midden in een dagstart leest als "hij is stuk".
    eersteLading: laden && !ooitGeladen,
    verversend: laden && ooitGeladen,
    fout,
    personenFout,
    assignees: server.assignees || [],
    instelbaarVerborgen: server.instelbaarVerborgen || [],
    takenPerPersoon,
    horizonPerPersoon,
    agendaUren,
    agendaFout,
    personen,
    ververs: () => haalOp({ ververs: true }),
    zetLokaal: zetOverlay,
    werkTaakBij,
    verzetInBulk,
    maakOngedaan,
  };
}
